import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    ReferenceField,
} from 'react-admin';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Divider,
    makeStyles,
    Button,
} from '@material-ui/core';
import {
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Person as PersonIcon,
    School as CourseIcon,
} from '@material-ui/icons';
import { useRecordContext } from 'react-admin';
import { useDataProvider } from 'react-admin';
import { useNotify } from 'react-admin';
import { useRefresh } from 'react-admin';
import { useTranslate } from 'react-admin';

const useStyles = makeStyles(theme => ({
    card: {
        marginBottom: theme.spacing(2),
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    ratingContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    ratingNumber: {
        fontSize: '2rem',
        fontWeight: 'bold',
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    stars: {
        display: 'flex',
        alignItems: 'center',
        marginRight: theme.spacing(2),
    },
    commentBox: {
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.grey[200]}`,
        marginBottom: theme.spacing(2),
    },
    statusChip: {
        textTransform: 'capitalize',
    },
    acceptedChip: {
        backgroundColor: theme.palette.success?.main || '#4caf50',
        color: 'white',
    },
    pendingChip: {
        backgroundColor: theme.palette.warning?.main || '#ff9800',
        color: 'white',
    },
    rejectedChip: {
        backgroundColor: theme.palette.error?.main || '#f44336',
        color: 'white',
    },
    infoSection: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    infoIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
}));

const ActionButtons = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [isLoading, setIsLoading] = React.useState(false);

    const handleAction = async (newStatus: 'accepted' | 'rejected') => {
        try {
            setIsLoading(true);
            if (newStatus === 'accepted') {
                await dataProvider.update('reviews', {
                    id: `${record.id}/accept`,
                    data: {},
                });

                notify(
                    `Review ${
                        newStatus === 'accepted' ? 'accepted' : 'rejected'
                    } successfully`,
                    { type: 'success' }
                );
            } else {
                await dataProvider.update('reviews', {
                    id: `${record.id}/reject`,
                    data: {},
                });
                notify(`Review rejected successfully`, { type: 'success' });
            }
            refresh();
        } catch (error) {
            notify('Error updating course status', { type: 'error' });
        }
        setIsLoading(false);
    };

    return record.status === 'pending' ? (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                gap: '8px',
            }}
        >
            <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleAction('rejected')}
                disabled={isLoading}
            >
                {translate('resources.reviews.actions.reject')}
            </Button>
            <Button
                variant="contained"
                color="primary"
                onClick={() => handleAction('accepted')}
                disabled={isLoading}
            >
                {translate('resources.reviews.actions.accept')}
            </Button>
        </div>
    ) : null;
};

// Custom component for rating display
const RatingField = ({ record, source }: any) => {
    const classes = useStyles();
    const rating = useRecordContext(record)?.[source];

    const renderStars = () => {
        const stars: React.ReactElement[] = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                i <= rating ? (
                    <StarIcon key={i} style={{ color: '#ffc107' }} />
                ) : (
                    <StarBorderIcon key={i} style={{ color: '#ffc107' }} />
                )
            );
        }
        return stars;
    };

    return (
        <Box className={classes.ratingContainer}>
            <Typography className={classes.ratingNumber}>{rating}</Typography>
            <Box className={classes.stars}>{renderStars()}</Box>
            <Typography variant="body2" color="textSecondary">
                ({rating}/5)
            </Typography>
        </Box>
    );
};

// Custom component for status display
const StatusField = ({ record, source }: any) => {
    const classes = useStyles();
    const status = useRecordContext(record)?.[source];

    const getStatusStyle = status => {
        switch (status) {
            case 'accepted':
                return classes.acceptedChip;
            case 'pending':
                return classes.pendingChip;
            case 'rejected':
                return classes.rejectedChip;
            default:
                return {};
        }
    };

    return (
        <Chip
            label={status}
            className={`${classes.statusChip} ${getStatusStyle(status)}`}
            size="small"
        />
    );
};

// Custom component for comment display
const CommentField = ({ record, source }: any) => {
    const classes = useStyles();
    const comment = useRecordContext(record)?.[source];

    if (!comment) return null;

    return (
        <Box className={classes.commentBox}>
            <Typography variant="h6" gutterBottom>
                Comment
            </Typography>
            <Typography variant="body1">"{comment}"</Typography>
        </Box>
    );
};

const LivreurField = () => {
    const record = useRecordContext();
    const classes = useStyles();

    if (!record || !record.created_for) return null;

    return record.created_for === 'Livreur' ? (
        <Box className={classes.infoSection}>
            <PersonIcon className={classes.infoIcon} />
            <Typography variant="body2" style={{ marginRight: 8 }}>
                Livreur :
            </Typography>
            <ReferenceField source="for" reference="livreurs" link="show">
                <TextField source="name" />
            </ReferenceField>
        </Box>
    ) : null;
};

const ReviewShow = props => {
    const classes = useStyles();

    return (
        <Show {...props} title="Review Details">
            <SimpleShowLayout>
                <Card className={classes.card}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between">
                            <ActionButtons />
                        </Box>
                        <Typography variant="h5" className={classes.header}>
                            Review Details
                        </Typography>

                        {/* Rating Section */}
                        <Box className={classes.section}>
                            <Typography variant="h6" gutterBottom>
                                Rating
                            </Typography>
                            <RatingField source="note" />
                        </Box>

                        <Divider />

                        {/* Comment Section */}
                        <Box
                            className={classes.section}
                            style={{ marginTop: 16 }}
                        >
                            <CommentField source="comment" />
                        </Box>

                        <Divider />

                        {/* Status Section */}
                        <Box
                            className={classes.section}
                            style={{ marginTop: 16 }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Status
                            </Typography>
                            <StatusField source="status" />
                        </Box>

                        <Divider />

                        {/* Reference Information */}
                        <Box
                            className={classes.section}
                            style={{ marginTop: 16 }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Information
                            </Typography>

                            <Box className={classes.infoSection}>
                                <PersonIcon className={classes.infoIcon} />
                                <Typography
                                    variant="body2"
                                    style={{ marginRight: 8 }}
                                >
                                    Reviewer:
                                </Typography>
                                <ReferenceField
                                    source="sender"
                                    reference="users"
                                    link="show"
                                >
                                    <TextField source="name" />
                                </ReferenceField>
                            </Box>

                            <Box className={classes.infoSection}>
                                <CourseIcon className={classes.infoIcon} />
                                <Typography
                                    variant="body2"
                                    style={{ marginRight: 8 }}
                                >
                                    Course:
                                </Typography>
                                <ReferenceField
                                    source="course"
                                    reference="courses"
                                    link="show"
                                >
                                    <TextField source="code" />
                                </ReferenceField>
                            </Box>
                            <LivreurField />

                            <Box className={classes.infoSection}>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Review for:{'  '}
                                    {/* This will show the record.created_for value */}
                                </Typography>
                                <TextField source="created_for" />
                            </Box>
                        </Box>

                        <Divider />

                        {/* Date Information */}
                        <Box
                            className={classes.section}
                            style={{ marginTop: 16 }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Timeline
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Created on:
                            </Typography>
                            <DateField
                                source="created_at"
                                showTime
                                options={{
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hourCycle: 'h23',
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </SimpleShowLayout>
        </Show>
    );
};

export default ReviewShow;
